import { z } from "zod";
import { GAME_CODES } from "./games";

export const gameCodeSchema = z.enum(GAME_CODES);

export const drawPrizeSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  amountVnd: z.number().int().nonnegative().nullable(),
  winnerCount: z.number().int().nonnegative().nullable(),
  jackpotVnd: z.number().int().nonnegative().nullable(),
  position: z.number().int().positive()
});

export const drawSchema = z.object({
  id: z.string().uuid(),
  gameCode: gameCodeSchema,
  drawNo: z.string().min(1),
  drawAt: z.string().datetime({ offset: true }),
  mainNumbers: z.array(z.number().int().positive()),
  specialNumber: z.number().int().positive().nullable(),
  prizes: z.array(drawPrizeSchema),
  sourcePageUrl: z.string().url(),
  sourcePdfUrl: z.string().url().nullable(),
  verificationStatus: z.enum(["pending", "verified", "rejected"]),
  retrievedAt: z.string().datetime({ offset: true })
});

export const modelMetricsSchema = z.object({
  brierScore: z.number().nonnegative(),
  logLoss: z.number().nonnegative(),
  recallAtK: z.number().min(0).max(1),
  uniformBrierScore: z.number().nonnegative(),
  sampleSize: z.number().int().nonnegative(),
  beatsUniformBaseline: z.boolean()
});

export const scoredNumberSchema = z.object({
  value: z.number().int().positive(),
  score: z.number().min(0).max(100),
  rank: z.number().int().positive(),
  kind: z.enum(["main", "special"])
});

export const predictionSetSchema = z.object({
  rank: z.number().int().positive(),
  mainNumbers: z.array(z.number().int().positive()),
  specialNumber: z.number().int().positive().nullable(),
  score: z.number().min(0).max(100)
});

export const predictionSchema = z.object({
  id: z.string().uuid(),
  gameCode: gameCodeSchema,
  targetDrawNo: z.string().min(1),
  targetDrawAt: z.string().datetime({ offset: true }),
  modelVersion: z.string().min(1),
  modelLabel: z.string().min(1),
  generatedAt: z.string().datetime({ offset: true }),
  dataCutoffAt: z.string().datetime({ offset: true }),
  numbers: z.array(scoredNumberSchema),
  sets: z.array(predictionSetSchema).length(5),
  metrics: modelMetricsSchema,
  disclaimer: z.string().min(1)
});

export const predictionSetEvaluationSchema = predictionSetSchema.extend({
  matchedMainNumbers: z.array(z.number().int().positive()),
  specialMatched: z.boolean()
});

export const predictionEvaluationSchema = z.object({
  id: z.string().uuid(),
  gameCode: gameCodeSchema,
  targetDrawNo: z.string().min(1),
  targetDrawAt: z.string().datetime({ offset: true }),
  modelVersion: z.string().min(1),
  modelLabel: z.string().min(1),
  generatedAt: z.string().datetime({ offset: true }),
  resultDraw: drawSchema,
  sets: z.array(predictionSetEvaluationSchema).length(5)
});

export const predictionEvaluationPageSchema = z.object({
  items: z.array(predictionEvaluationSchema),
  nextOffset: z.number().int().nonnegative().nullable()
});

export const predictionEvaluationHistoryItemSchema = z.discriminatedUnion("status", [
  predictionEvaluationSchema.extend({
    status: z.literal("evaluated")
  }),
  z.object({
    status: z.literal("prediction_missing"),
    id: z.string().uuid(),
    gameCode: gameCodeSchema,
    targetDrawNo: z.string().min(1),
    targetDrawAt: z.string().datetime({ offset: true }),
    resultDraw: drawSchema
  })
]);

export const predictionEvaluationHistoryPageSchema = z.object({
  items: z.array(predictionEvaluationHistoryItemSchema),
  nextOffset: z.number().int().nonnegative().nullable()
});

export const profileSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().min(1).max(80).nullable(),
  favoriteGame: gameCodeSchema.nullable(),
  adultAcknowledgedAt: z.string().datetime({ offset: true }),
  termsVersion: z.string().min(1),
  privacyVersion: z.string().min(1),
  createdAt: z.string().datetime({ offset: true })
});

export const savedSetSchema = z.object({
  id: z.string().uuid(),
  gameCode: gameCodeSchema,
  predictionId: z.string().uuid().nullable(),
  label: z.string().max(80).nullable(),
  mainNumbers: z.array(z.number().int().positive()),
  specialNumber: z.number().int().positive().nullable(),
  createdAt: z.string().datetime({ offset: true })
});

export const notificationPreferenceSchema = z.object({
  gameCode: gameCodeSchema,
  predictionReady: z.boolean(),
  resultReady: z.boolean()
});

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string().optional()
  })
});

export type Draw = z.infer<typeof drawSchema>;
export type DrawPrize = z.infer<typeof drawPrizeSchema>;
export type ModelMetrics = z.infer<typeof modelMetricsSchema>;
export type ScoredNumber = z.infer<typeof scoredNumberSchema>;
export type PredictionSet = z.infer<typeof predictionSetSchema>;
export type Prediction = z.infer<typeof predictionSchema>;
export type PredictionSetEvaluation = z.infer<typeof predictionSetEvaluationSchema>;
export type PredictionEvaluation = z.infer<typeof predictionEvaluationSchema>;
export type PredictionEvaluationPage = z.infer<typeof predictionEvaluationPageSchema>;
export type PredictionEvaluationHistoryItem = z.infer<
  typeof predictionEvaluationHistoryItemSchema
>;
export type PredictionEvaluationHistoryPage = z.infer<
  typeof predictionEvaluationHistoryPageSchema
>;
export type UserProfile = z.infer<typeof profileSchema>;
export type SavedSet = z.infer<typeof savedSetSchema>;
export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>;
export type ApiError = z.infer<typeof apiErrorSchema>;
